import { useEffect, useState } from 'react'
import { ThreeDots } from 'react-loader-spinner'
import Lightbox from './Lightbox'

const CATHOLIC_DESIGNS_API_URL = 'https://script.google.com/macros/s/AKfycbwQfFwCqJoTP0xBdBFpPAiDuFg_GRSWAycC7hW10sa_5Z5WrXWmMsc5ZNXjnjNUA6rieQ/exec'

const FALLBACK_PLACEHOLDER = 'https://via.placeholder.com/400x500/8B7355/ffffff?text=Image+Not+Found'

const getImageSources = (image) =>
  [
    image?.lh3Url,
    image?.userContentUrl,
    image?.imageUrl,
    image?.thumbnailUrl,
    image?.downloadUrl
  ].filter(Boolean)

const getPreferredImageUrl = (image) => getImageSources(image)[0] || ''

const getImageCaption = (image, index) => image?.name || `Catholic Design ${index + 1}`

const CatholicDesignsGallery = ({ category, onImagesLoaded }) => {
  const [galleryImages, setGalleryImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [lightboxData, setLightboxData] = useState({
    isOpen: false,
    src: '',
    caption: '',
    images: [],
    currentIndex: 0
  })

  useEffect(() => {
    let cancelled = false

    const callbackName =
      `catholicDesignsCallback_${category}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`

    const script = document.createElement('script')
    const separator = CATHOLIC_DESIGNS_API_URL.includes('?') ? '&' : '?'

    setLoading(true)
    setError('')
    setGalleryImages([])

    window[callbackName] = (data) => {
      if (!cancelled) {
        if (Array.isArray(data)) {
          setGalleryImages(data)
          setError('')
        } else {
          console.error('Catholic Designs API error:', data)
          setGalleryImages([])
          setError(
            data?.message || 'Catholic Designs images could not be loaded.'
          )
        }

        setLoading(false)
      }

      script.remove()

      setTimeout(() => {
        if (window[callbackName]) {
          delete window[callbackName]
        }
      }, 1000)
    }

    script.src =
      `${CATHOLIC_DESIGNS_API_URL}` +
      `${separator}type=catholic` +
      `&category=${encodeURIComponent(category)}` +
      `&callback=${encodeURIComponent(callbackName)}` +
      `&_=${Date.now()}`

    script.async = true

    script.onerror = () => {
      if (!cancelled) {
        setGalleryImages([])
        setLoading(false)
        setError('Catholic Designs images could not be loaded.')
      }

      script.remove()

      setTimeout(() => {
        if (window[callbackName]) {
          delete window[callbackName]
        }
      }, 1000)
    }

    document.body.appendChild(script)

    return () => {
      cancelled = true
      script.remove()

      // Do not immediately delete the callback here.
      // Replace it temporarily with a no-op because the remote JSONP script
      // may still execute after React StrictMode cleanup.
      window[callbackName] = () => {}

      setTimeout(() => {
        if (window[callbackName]) {
          delete window[callbackName]
        }
      }, 5000)
    }
  }, [category])

  const openLightbox = (image, index) => {
    const lightboxImages = galleryImages.map((item) => ({
      src: getPreferredImageUrl(item) || FALLBACK_PLACEHOLDER,
      title: item?.name || 'Catholic Design'
    }))

    setLightboxData({
      isOpen: true,
      src: getPreferredImageUrl(image) || FALLBACK_PLACEHOLDER,
      caption: getImageCaption(image, index),
      images: lightboxImages,
      currentIndex: index
    })
  }

  const closeLightbox = () => {
    setLightboxData({ isOpen: false, src: '', caption: '', images: [], currentIndex: 0 })
  }

  const navigateLightbox = (direction) => {
    const { images, currentIndex } = lightboxData
    if (!images.length) return

    const nextIndex =
      direction === 'next'
        ? (currentIndex + 1) % images.length
        : (currentIndex - 1 + images.length) % images.length

    const nextImage = images[nextIndex]
    setLightboxData({
      ...lightboxData,
      src: nextImage.src,
      caption: nextImage.title,
      currentIndex: nextIndex
    })
  }

  const getInitialImageSrc = (image) => {
    const sources = getImageSources(image)
    return sources.length > 0 ? sources[0] : FALLBACK_PLACEHOLDER
  }

  const handleImageError = (image) => (event) => {
    const img = event.currentTarget
    const imageSources = getImageSources(image)
    const currentIndex = Number(img.dataset.sourceIndex || 0)
    const nextIndex = currentIndex + 1

    if (nextIndex < imageSources.length) {
      img.dataset.sourceIndex = String(nextIndex)
      img.src = imageSources[nextIndex]
    } else {
      img.style.display = 'none'
    }
  }

  return (
    <div className="content-area">
      <div className="gallery-grid">
        {loading ? (
          <div className="gospel-loader">
            <ThreeDots
              height={60}
              width={60}
              color="#ffa72c"
              secondaryColor="#ffa72c"
              visible={true}
              ariaLabel="catholic-gallery-loading"
            />
          </div>
        ) : error ? (
          <div className="no-data-message">{error}</div>
        ) : galleryImages.length === 0 ? (
          <div className="no-data-message">No images available for this category.</div>
        ) : (
          galleryImages.map((image, index) => (
            <button
              type="button"
              key={image?.id ?? `${category}-${index}`}
              className="gallery-card"
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => openLightbox(image, index)}
            >
              <img
                src={getInitialImageSrc(image)}
                alt={getImageCaption(image, index)}
                loading="lazy"
                referrerPolicy="no-referrer"
                data-source-index="0"
                onError={handleImageError(image)}
              />
            </button>
          ))
        )}
      </div>

      <Lightbox
        isOpen={lightboxData.isOpen}
        src={lightboxData.src}
        caption={lightboxData.caption}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
        hasMultipleImages={lightboxData.images.length > 1}
        currentIndex={lightboxData.currentIndex}
        totalImages={lightboxData.images.length}
      />
    </div>
  )
}

export default CatholicDesignsGallery
